<!-- components/FeedbackModal.vue -->
<script setup lang="ts">
import * as Sentry from '@sentry/nuxt'
import { toTypedSchema } from '@vee-validate/zod'
import { CheckCircle2, Loader2 } from 'lucide-vue-next'
import { vMaska } from 'maska/vue'
import { useForm } from 'vee-validate'
import { toast } from 'vue-sonner'
import { z } from 'zod'

const props = defineProps({
  modelValue: Boolean,
})

const emit = defineEmits(['update:modelValue'])
const success = ref(false)
const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const formSchema = toTypedSchema(
  z.object({
    name: z.string()
      .min(2, 'Минимум 2 символа')
      .max(50, 'Максимум 50 символов'),
    phoneNumber: z.string()
      .nonempty('Номер телефона обязателен')
      .transform(val => val.replace(/\D/g, ''))
      .refine(val => val === '' || val.length === 11, 'Номер должен содержать 11 цифр'),
    message: z.string()
      .min(5, 'Минимум 5 символов')
      .max(250, 'Максимум 250 символов'),
  }),
)

const { handleSubmit, isSubmitting, meta, errors, resetForm } = useForm({
  validationSchema: formSchema,
  initialValues: {
    name: '',
    phoneNumber: '',
    message: '',
  },
})

const { apiBase } = useRuntimeConfig().public

const onSubmit = handleSubmit(async (values) => {
  try {
    await $fetch(`${apiBase}/lk/feedback/send`, {
      method: 'post',
      body: {
        name: values.name,
        phone_number: values.phoneNumber || undefined,
        feedback_message: values.message,
      },
    })

    // Логируем успешную отправку
    Sentry.addBreadcrumb({
      category: 'feedback',
      message: 'Feedback submitted successfully',
      level: 'info',
    })

    success.value = true
    resetForm()
  }
  catch (err: any) {
    console.error(err)

    // Отправляем ошибку в Sentry с контекстом
    Sentry.withScope((scope) => {
      scope.setTag('component', 'FeedbackModal')
      scope.setTag('action', 'feedback_submit')
      scope.setContext('feedback_data', {
        has_name: !!values.name,
        has_phone: !!values.phoneNumber,
        message_length: values.message?.length || 0,
      })
      Sentry.captureException(err)
    })

    let errorMessage = 'Неизвестная ошибка'
    if (err instanceof Error && err.message) {
      errorMessage = err.message
    }
    if (err.response?._data?.detail?.error_msg) {
      errorMessage = err.response._data.detail.error_msg
    }
    toast.error('Ошибка', {
      description: errorMessage,
    })
  }
})

watch(isOpen, (val) => {
  if (!val) {
    success.value = false
    resetForm()
  }
})
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent v-if="!success" class="md:max-w-lg" @open-auto-focus.prevent>
      <DialogHeader>
        <DialogTitle>Оставить отзыв</DialogTitle>
        <DialogDescription>
          Поделитесь своим мнением о нас, чтобы мы могли стать лучше
        </DialogDescription>
      </DialogHeader>

      <form @submit="onSubmit">
        <div class="grid gap-4 py-4">
          <FormField v-slot="{ componentField }" name="name">
            <FormItem>
              <FormControl>
                <FormInput
                  id="name"
                  label="Имя *"
                  placeholder="Введите ваше имя"
                  :error="!!errors.name"
                  v-bind="componentField"
                  :disabled="isSubmitting"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="phoneNumber">
            <FormItem>
              <FormControl>
                <FormInput
                  id="phone"
                  v-maska="{
                    mask: '+7 (9##) ###-##-##',
                  }"
                  label="Номер телефона*"
                  placeholder="+7 (999) 999-99-99"
                  :error="!!errors.phoneNumber"
                  v-bind="componentField"
                  :disabled="isSubmitting"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="message">
            <FormItem>
              <FormLabel>Сообщение *</FormLabel>
              <FormControl>
                <Textarea
                  id="message"
                  placeholder="Введите ваше сообщение"

                  class="min-h-[100px] bg-slate-50 focus:bg-inherit text-black"
                  v-bind="componentField"
                  :disabled="isSubmitting"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <DialogFooter>
          <Button
            type="submit"
            class="w-full bg-primary hover:bg-primary/90"
            :disabled="isSubmitting || !meta.dirty || !meta.valid"
          >
            <span v-if="!isSubmitting">Отправить</span>
            <Loader2 v-else class="h-4 w-4 animate-spin" />
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>

    <DialogContent v-else class="md:max-w-lg" @open-auto-focus.prevent>
      <div class="flex flex-col items-center gap-4">
        <CheckCircle2 class="h-12 w-12 text-green-500" />
        <h3 class="text-xl font-semibold">
          Спасибо за ваше сообщение!
        </h3>
        <p class="text-center">
          Если у вас срочный вопрос, вы можете связаться с нами по телефону
          <a href="tel:88007707345" class="text-primary">8 (800) 770-73-45 </a>
        </p>
      </div>
    </DialogContent>
  </Dialog>
</template>
